import { cloneElement, isValidElement } from 'react'
import type { ComponentProps, ReactElement } from 'react'
import { classes } from 'src/utils'
import type { OptionalProps } from 'src/utils'
import { Spinner } from '../Shared'

type Props = {
  label: string
  isPrimary?: boolean
  isLoading?: boolean
  loadingLabel?: string
  disabled?: boolean
  className?: string
} & (
  | { type: 'submit'; onClick?: undefined }
  | { type?: 'button'; onClick: () => void }
  | { type: 'menu'; onClick?: undefined }
) &
  OptionalProps<{
    icon: ReactElement<ComponentProps<'svg'>>
    iconPosition: 'left' | 'right'
  }>

const ButtonSpinner = (
  <div className="px-0.5">
    <Spinner />
  </div>
)

export function Button(props: Props) {
  const disabled = props.isLoading || props.disabled

  const Icon =
    isValidElement(props.icon) &&
    cloneElement(props.icon, {
      className: classes(
        'w-4 h-4 pointer-events-none',
        disabled
          ? 'text-slate-300'
          : props.isPrimary
          ? 'text-white'
          : 'text-slate-400',
      ),
      'aria-hidden': true,
    })

  return (
    <button
      onClick={props.onClick}
      type={props.type === 'menu' ? 'button' : props.type}
      disabled={disabled}
      className={classes(
        'cursor-pointer relative h-10 py-2 text-left rounded flex flex-row items-center hover:shadow-sm active:shadow-none',
        props.isPrimary
          ? 'bg-theme-500 text-white hover:bg-theme-600 active:bg-theme-700 disabled:bg-slate-100 disabled:text-slate-400'
          : 'bg-white text-slate-600 border border-slate-100 hover:border-slate-200 active:bg-slate-100 active:border-slate-100 disabled:bg-white disabled:text-slate-400 disabled:border-slate-100',
        // 'hover:shadow hover:ring-1 hover:ring-slate-200 active:ring-slate-100 active:ring-1',
        'disabled:shadow-none disabled:cursor-not-allowed',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-theme-500 focus-visible:border-theme-500',
        'disabled:focus-visible:border-slate-300 disabled:focus-visible:ring-slate-300',
        props.iconPosition === undefined ? 'px-3' : '',
        props.iconPosition === 'left' ? 'px-2' : '',
        props.iconPosition === 'right' ? 'px-2' : '',
        props.className,
      )}
    >
      {props.iconPosition === 'left' &&
        (props.isLoading ? ButtonSpinner : Icon)}

      {props.iconPosition === undefined &&
        (props.isLoading ? ButtonSpinner : null)}

      <span className="block px-2 text-sm font-semibold leading-5 capitalize truncate">
        {props.loadingLabel && props.isLoading
          ? props.loadingLabel
          : props.label}
      </span>

      {props.iconPosition === 'right' &&
        (props.isLoading ? ButtonSpinner : Icon)}
    </button>
  )
}
